import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { ThemeProvider } from '@mui/material/styles';

import Contexts from './Contexts/Contexts';
import useLocalStorage from '../Util/custom-hooks/UseLocalStorage';
import EventManager from '../Util/EventManager';
import Routes from './Routes';
import theme from './theme';

function PageTracker() {
    const location = useLocation();
    useEffect(() => {
        window.gtag('event', 'page_view', {
            page_path: location.pathname + location.search,
        });
    }, [location]);
    return null;
}

function App() {
    const [user, setUser] = useLocalStorage('user', {});
    const [message, setMessage] = useState('');
    const selfDestructMessageTimeout = useRef();
    const eventRef = useRef();

    const selfDestructMessage = (message) => {
        setMessage(message);
        if (selfDestructMessageTimeout.current) {
            clearTimeout(selfDestructMessageTimeout.current);
        }
        selfDestructMessageTimeout.current = setTimeout(() => setMessage(''), 10000);
    };

    useEffect(() => {
        const chatEvent = (e) => {
            EventManager.emitChatEvent();
        };

        const roomEvent = (e) => {
            /*
                Room event schema:
                {
                    eventClass: "RoomEventType"
                    eventTimestamp: "2021-05-01T17:26:55.117602"
                    eventType: "INDEX_CHANGE"
                    payload: {...},
                    receivers: [...],
                    sender: {...}
                }
            */
            EventManager.emitRoomEvent(JSON.parse(e.data));
        };

        const pathEvent = (e) => {
            EventManager.emitPathEvent(e);
        };

        const connectEvent = ({ data }) => {
            const user = JSON.parse(data);
            EventManager.emitConnectEvent(user);
        };

        const slideReactionEvent = (e) => {
            EventManager.emitSlideReactionEvent(JSON.parse(e.data));
        };

        if (user.userId) {
            const eventsUrl = process.env.REACT_APP_EVENTS_HOST
                ? `${process.env.REACT_APP_EVENTS_HOST}/events/${user.userId}`
                : `/events/${user.userId}`;
            eventRef.current = new EventSource(eventsUrl);
            eventRef.current.addEventListener('RoomEventType', roomEvent);
            eventRef.current.addEventListener('ChatMessageEventType', chatEvent);
            eventRef.current.addEventListener('PresentationTrailEvent', pathEvent);
            eventRef.current.addEventListener('ConnectEventType', connectEvent);
            eventRef.current.addEventListener('SlideReactionEventType', slideReactionEvent);
            eventRef.current.onerror = (e) => {
                console.log('Error from event source');
            };

            eventRef.current.onopen = (e) => {
                console.log('event source connection open');
            };
            eventRef.current.onclose = (e) => {
                console.log('event source connection CLOSED');
            };
        }
        return () => {
            eventRef.current && eventRef.current.close();
        };
    }, [user, user.userId]);

    useEffect(() => {
        EventManager.listenLoginUser((data) => {
            setUser(data);
        });
        EventManager.listenKillUser(() => {
            setUser('user', {});
            window.location.replace(`/welcome`);
        });
        return () => {
            EventManager.unsetKillUser();
            EventManager.unsetLoginUser();
        };
    }, [setUser]);

    return (
        <ThemeProvider theme={theme}>
            <Contexts
                userContext={user}
                messageContext={{ message, setMessage: selfDestructMessage }}>
                <BrowserRouter>
                    <PageTracker />
                    <Routes />
                </BrowserRouter>
                <Snackbar
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    open={!!message}
                    onClose={(event, reason) => {
                        if (reason === 'clickaway') {
                            return;
                        }
                        setMessage('');
                    }}
                    message={message}
                    action={
                        <IconButton
                            aria-label='close'
                            color='inherit'
                            onClick={() => setMessage('')}>
                            <CloseIcon />
                        </IconButton>
                    }
                />
            </Contexts>
        </ThemeProvider>
    );
}

export default App;
